/**
 * LegalPrimitives — Sprint D (Feb 2026)
 *
 * Shared components for /terms, /privacy, /cookies, /guidelines,
 * plus the /safety hub + article pages.
 *
 * Design tokens locked to mobile parity per LEGAL_PAGES_HANDOFF.md.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Info } from 'lucide-react';

/* ============================================================ */
/* Screen chrome                                                */
/* ============================================================ */

export const LEGAL_BG = '#FFFFFF';

export function LegalHeader({ title, backTo }) {
  const navigate = useNavigate();
  return (
    <div
      className="sticky top-0 z-30 flex items-center gap-2 px-3 py-3"
      style={{
        background: '#FFFFFF',
        borderBottom: '1px solid rgba(15,15,16,0.06)',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
      }}
    >
      <button
        onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
        aria-label="Back"
        data-testid="legal-back-btn"
        className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/[0.04] transition-colors"
      >
        <ArrowLeft size={22} className="text-[#0F0F10]" strokeWidth={2.4} />
      </button>
      <h1
        className="flex-1 text-center text-[17px] font-[900] text-[#0F0F10] truncate"
        style={{ letterSpacing: '-0.2px' }}
      >
        {title}
      </h1>
      <div className="w-10" />
    </div>
  );
}

/* ============================================================ */
/* Hero card                                                     */
/* ============================================================ */

export function HeroCard({ icon: Icon, title, lastUpdated }) {
  return (
    <div className="flex flex-col items-center text-center py-6 mb-2" data-testid="legal-hero">
      <div
        className="w-[68px] h-[68px] rounded-2xl flex items-center justify-center mb-4"
        style={{
          background: 'linear-gradient(135deg, #DC2626 0%, #7E22CE 50%, #1E4FD8 100%)',
          boxShadow: '0 8px 14px 0 rgba(220,38,38,0.4)',
        }}
      >
        <Icon size={30} className="text-white" strokeWidth={2.2} />
      </div>
      <h2
        className="text-[22px] font-[900] text-[#0F0F10] px-8"
        style={{ letterSpacing: '-0.4px' }}
      >
        {title}
      </h2>
      {lastUpdated && (
        <p className="text-[12px] font-[600] text-[#6B7280] mt-1.5" data-testid="legal-last-updated">
          Last Updated: {lastUpdated}
        </p>
      )}
    </div>
  );
}

/* ============================================================ */
/* Language chip bar                                             */
/* ============================================================ */

export const LEGAL_TRANSLATIONS = {
  terms:      { en: true, es: false, 'pt-BR': false, fr: false },
  privacy:    { en: true, es: false, 'pt-BR': false, fr: false },
  cookies:    { en: true, es: false, 'pt-BR': false, fr: false },
  guidelines: { en: true, es: false, 'pt-BR': false, fr: false },
};

const LANG_LABELS = { en: 'English', es: 'Español', 'pt-BR': 'Português', fr: 'Français' };

export function LegalLanguageBar({ docKey }) {
  const [active, setActive] = useState('en');
  const registry = LEGAL_TRANSLATIONS[docKey] || { en: true };

  return (
    <div className="mt-2 mb-6" data-testid="legal-language-bar">
      <p className="text-[11px] font-[700] tracking-[0.12em] uppercase text-[#6B7280] mb-2">
        Available in:
      </p>
      <div className="flex flex-wrap gap-2">
        {Object.entries(LANG_LABELS).map(([code, label]) => {
          const enabled = registry[code] === true;
          const isActive = enabled && active === code;
          return (
            <button
              key={code}
              onClick={() => enabled && setActive(code)}
              disabled={!enabled}
              data-testid={`legal-lang-${code}`}
              className="flex flex-col items-center justify-center rounded-full"
              style={{
                padding: '8px 12px',
                background: isActive ? '#0F0F10' : enabled ? '#FFFFFF' : '#F4F4F5',
                border: isActive ? 'none' : `1px solid ${enabled ? 'rgba(15,15,16,0.12)' : 'transparent'}`,
                color: isActive ? '#FFFFFF' : enabled ? '#374151' : '#9CA3AF',
                cursor: enabled ? 'pointer' : 'not-allowed',
              }}
            >
              <span className="text-[13px] font-[700]">{label}</span>
              {!enabled && (
                <span className="text-[9px] font-[600] mt-[1px]" style={{ color: '#9CA3AF' }}>
                  Coming soon
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================ */
/* Section / P / Bullet / SubHeading / do / don't                */
/* ============================================================ */

export function Section({ number, title, children }) {
  return (
    <section className="mt-6 mb-1" data-testid={`legal-section-${number}`}>
      <h3
        className="text-[17px] font-[900] text-[#0F0F10] mb-2.5"
        style={{ letterSpacing: '-0.3px' }}
      >
        {number}. {title}
      </h3>
      {children}
    </section>
  );
}

export function P({ children, className = '', bold = false }) {
  return (
    <p
      className={`text-[14.5px] mb-2.5 ${className}`}
      style={{ color: '#3F3F46', lineHeight: '22px', fontWeight: bold ? 700 : 400 }}
    >
      {children}
    </p>
  );
}

export function Bullet({ children, positive = false }) {
  return (
    <div className="flex gap-2 pl-1 mb-1.5">
      <span
        className="text-[14px] font-[900] flex-shrink-0"
        style={{ color: positive ? '#16A34A' : '#B91C1C', lineHeight: '22px' }}
      >
        •
      </span>
      <span className="text-[14px] flex-1" style={{ color: '#3F3F46', lineHeight: '22px' }}>
        {children}
      </span>
    </div>
  );
}

export function SubHeading({ children }) {
  return (
    <h4
      className="text-[14px] font-[900] text-[#0F0F10] mt-3 mb-1.5"
      style={{ letterSpacing: '-0.2px' }}
    >
      {children}
    </h4>
  );
}

export function DoHeading({ children }) {
  return (
    <h4
      className="text-[13px] font-[800] mt-2 mb-1"
      style={{ color: '#166534', letterSpacing: '0.2px' }}
    >
      {children}
    </h4>
  );
}

export function DontHeading({ children }) {
  return (
    <h4
      className="text-[13px] font-[800] mt-2 mb-1"
      style={{ color: '#B91C1C', letterSpacing: '0.2px' }}
    >
      {children}
    </h4>
  );
}

/* ============================================================ */
/* Contact box + email link                                      */
/* ============================================================ */

export function EmailLink({ label, address }) {
  return (
    <a
      href={`mailto:${address}`}
      className="text-[13.5px] block hover:opacity-80 transition-opacity"
      style={{ color: '#374151' }}
    >
      <span className="font-[600]">{label}: </span>
      <span className="underline">{address}</span>
    </a>
  );
}

export function ExternalLink({ label, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-[13.5px] block hover:opacity-80 transition-opacity"
      style={{ color: '#374151' }}
    >
      <span className="font-[600]">{label}: </span>
      <span className="underline">{href}</span>
    </a>
  );
}

export function ContactBox({ children }) {
  return (
    <div
      className="rounded-2xl p-4 space-y-2 mt-2"
      style={{ background: '#FFFFFF', border: '1px solid rgba(15,15,16,0.06)' }}
      data-testid="legal-contact-box"
    >
      {children}
    </div>
  );
}

/* ============================================================ */
/* Footer                                                        */
/* ============================================================ */

export function LegalFooter() {
  return (
    <footer
      className="mt-8 pt-5 text-center"
      style={{ borderTop: '1px solid rgba(15,15,16,0.06)' }}
      data-testid="legal-footer"
    >
      <p className="text-[11px] font-[600]" style={{ color: '#9CA3AF' }}>
        © {new Date().getFullYear()} Meet to Keep LLC. All rights reserved.
      </p>
    </footer>
  );
}

/* ============================================================ */
/* Page wrapper                                                  */
/* ============================================================ */

export function LegalPageShell({ title, backTo, children }) {
  return (
    <div className="min-h-screen pb-8" style={{ background: LEGAL_BG }}>
      <LegalHeader title={title} backTo={backTo} />
      <div className="max-w-2xl mx-auto px-5 pt-2">{children}</div>
    </div>
  );
}

/* ============================================================ */
/* Info banner (used for language fallback + safety notices)     */
/* ============================================================ */

export function InfoBanner({ children }) {
  return (
    <div
      className="flex items-start gap-2 rounded-xl p-3 mb-4"
      style={{ background: '#EFF6FF', border: '1px solid #DBEAFE' }}
    >
      <Info size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#1D4ED8' }} />
      <p className="text-[12.5px] flex-1" style={{ color: '#1E3A8A' }}>
        {children}
      </p>
    </div>
  );
}

/* ============================================================ */
/* Emergency-style callout (used across safety pages)            */
/* ============================================================ */

/* Icon-prefixed callout used by /safety/* articles.                    */
/* tone: info (blue), warning (amber), success (green).                 */
export function Callout({ tone = 'info', children }) {
  const palette =
    tone === 'warning'
      ? { bg: '#FFFBEB', border: '#FDE68A', fg: '#B45309', icon: '\u26A0' /* ⚠ */ }
      : tone === 'success'
      ? { bg: '#F0FDF4', border: '#BBF7D0', fg: '#166534', icon: '\u2705' /* ✅ */ }
      : { bg: '#EFF6FF', border: '#DBEAFE', fg: '#1D4ED8', icon: '\uD83D\uDCD8' /* 📘 */ };
  return (
    <div
      className="flex items-start gap-2.5 rounded-xl p-3.5 mb-3 mt-1"
      style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
    >
      <span className="text-[16px] leading-[22px] flex-shrink-0">{palette.icon}</span>
      <p className="text-[13.5px] font-[700] flex-1" style={{ color: palette.fg, lineHeight: '20px' }}>
        {children}
      </p>
    </div>
  );
}

/* Topic card wrapper used by /safety/healthy-dating.                   */
export function TopicCard({ number, icon: Icon, gradient, title, children }) {
  return (
    <section className="mt-6 mb-1" data-testid={`safety-topic-${number}`}>
      <div className="flex items-center gap-2.5 mb-3">
        {Icon && (
          <span
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: gradient || 'linear-gradient(135deg, #DC2626, #7E22CE)' }}
          >
            <Icon size={16} className="text-white" strokeWidth={2.4} />
          </span>
        )}
        <h3 className="text-[17px] font-[900] text-[#0F0F10]" style={{ letterSpacing: '-0.3px' }}>
          {title}
        </h3>
      </div>
      {children}
    </section>
  );
}

export function CalloutCard({ tone = 'red', label, children }) {
  const palette =
    tone === 'red'
      ? { bg: '#FEF2F2', border: '#FEE2E2', label: '#B91C1C', body: '#0F0F10' }
      : tone === 'amber'
      ? { bg: '#FFFBEB', border: '#FDE68A', label: '#B45309', body: '#0F0F10' }
      : tone === 'green'
      ? { bg: '#F0FDF4', border: '#BBF7D0', label: '#166534', body: '#0F0F10' }
      : { bg: '#EFF6FF', border: '#DBEAFE', label: '#1D4ED8', body: '#0F0F10' };
  return (
    <div
      className="rounded-2xl p-4 mb-3"
      style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
    >
      {label && (
        <p
          className="text-[10.5px] font-[900] uppercase tracking-[0.08em] mb-1.5"
          style={{ color: palette.label }}
        >
          {label}
        </p>
      )}
      <div className="text-[15px] font-[700]" style={{ color: palette.body, lineHeight: '22px' }}>
        {children}
      </div>
    </div>
  );
}
